import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Box,
	Button,
	CircularProgress,
	Paper,
	Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { ErrorModal, HelmetDexteel, MesfModal } from "@dexteel/mesf-core";
import {
	Group,
	Panel,
	Separator,
	useDefaultLayout,
} from "react-resizable-panels";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { DeleteConfirmModal } from "@/controls/modals/DeleteConfirmModal";
import { useConfigurationDelayCodeContext } from "../context/ConfigurationDelayCodeContext";
import { useSearchDelays } from "../hooks/delays/useSearchDelays";
import {
	deleteDelay,
	upsertDelay,
} from "../repositories/ConfigurationDelayRepository";
import { CatalogDelayCode } from "../models/CatalogDelayCode";
import { TreeViewDelay } from "./DelayConfiguration/TreeViewDelay";
import { DelayCodeDetailViewer } from "./DelayCodeDetailViewer";
import { DelayCodesSearchInput } from "./DelayCodesSearchInput";
import {
	SHOW_ONLY_ACTIVES_DEFAULT,
	ShowOnlyActivesCheckbox,
} from "@/controls/filters/ShowOnlyActivesCheckbox";
import { urlParam, useUrlState } from "@/shared/hooks/useUrlState";

const DEFAULT_LAYOUT = { left: 60, right: 40 };

// The selected node travels as its id. Which branches are open is view state: the tree
// opens the path to the node on its own.
const URL_SCHEMA = {
	onlyActives: urlParam.bool(SHOW_ONLY_ACTIVES_DEFAULT),
	nodeId: urlParam.int(),
};

interface DelayCodesManagerProps {
	delayAreaAssetId?: number;
	delayAreaAssetName?: string;
}

export const DelayCodesManager = ({
	delayAreaAssetId,
	delayAreaAssetName,
}: DelayCodesManagerProps) => {
	const [error, setError] = useState("");
	const [notification, setNotification] = useState("");
	const [selectedNode, setSelectedNode] = useState<CatalogDelayCode | null>(
		null,
	);
	const [isCreating, setIsCreating] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [nodeToDelete, setNodeToDelete] = useState<CatalogDelayCode | null>(
		null,
	);
	const [isDeleting, setIsDeleting] = useState(false);
	const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
	const [nodeToDeactivate, setNodeToDeactivate] =
		useState<CatalogDelayCode | null>(null);
	const [isDeactivating, setIsDeactivating] = useState(false);
	const [scrollToNodeId, setScrollToNodeId] = useState<number | null>(null);
	const [{ onlyActives, nodeId }, setFilters, { dropValues }] =
		useUrlState(URL_SCHEMA);

	const {
		state: { allDelayCodes, openDelays },
		actions: { setDelayNodeSelectedInTree },
	} = useConfigurationDelayCodeContext();

	// The panel keeps its own copy of the node, so an autosave that refreshes the tree does
	// not reload it; the URL only carries the id.
	const showNode = useCallback(
		(node: CatalogDelayCode | null) => {
			setSelectedNode(node);
			if (node?.DelayCodeId != null) setFilters({ nodeId: node.DelayCodeId });
			else dropValues("nodeId");
		},
		[setFilters, dropValues],
	);

	// The id in the URL changed without a click here (a link, or Back to the page): open
	// the path to that node and select it. It waits for the tree to open its roots on
	// load, which replaces the open branches and would otherwise close the path again.
	const codesLoaded =
		Array.isArray(allDelayCodes) && Object.keys(openDelays).length > 0;
	// biome-ignore lint/correctness/useExhaustiveDependencies: only the URL or the first load starts this
	useEffect(() => {
		if (!codesLoaded) return;
		if ((selectedNode?.DelayCodeId ?? null) === nodeId) return;
		if (nodeId === null) {
			if (!isCreating) setSelectedNode(null);
			return;
		}
		const node = (allDelayCodes as CatalogDelayCode[]).find(
			(n) => n.DelayCodeId === nodeId,
		);
		if (!node) {
			dropValues("nodeId");
			return;
		}
		setIsCreating(false);
		setSelectedNode(node);
		setDelayNodeSelectedInTree(node);
		setScrollToNodeId(nodeId);
	}, [codesLoaded, nodeId]);

	// The search box must not offer a node the tree is currently hiding.
	// allDelayCodes is not an array until the first fetch resolves.
	const searchableDelayCodes = useMemo(() => {
		const codes: CatalogDelayCode[] = Array.isArray(allDelayCodes)
			? allDelayCodes
			: [];
		return onlyActives ? codes.filter((n) => n.IsActive !== false) : codes;
	}, [allDelayCodes, onlyActives]);

	const noopLoading = useRef(() => {}).current;
	const { searchDelays } = useSearchDelays({
		setSearchError: setError,
		setAllDelayLoading: noopLoading,
		includeInactive: true,
	});

	const { defaultLayout, onLayoutChange } = useDefaultLayout({
		id: "delay-codes-manager-v1",
		panelIds: ["left", "right"],
		storage: localStorage,
	});

	const handleNodeSelect = useCallback(
		(node: CatalogDelayCode) => {
			setIsCreating(false);
			// A later link to this same node has to scroll to it again.
			setScrollToNodeId(null);
			showNode(node);
		},
		[showNode],
	);

	const handleRequestAdd = useCallback(() => {
		showNode(null);
		setIsCreating(true);
	}, [showNode]);

	const handleRequestDelete = useCallback((node: CatalogDelayCode) => {
		setNodeToDelete(node);
		setDeleteModalOpen(true);
	}, []);

	const handleConfirmDelete = useCallback(async () => {
		if (!nodeToDelete?.DelayCodeId) return;
		setIsDeleting(true);
		const resp = await deleteDelay(nodeToDelete.DelayCodeId);
		if (resp.ok) {
			setNotification("Delay code deleted successfully");
			if (selectedNode?.DelayCodeId === nodeToDelete.DelayCodeId) {
				showNode(null);
				setIsCreating(false);
			}
			await searchDelays(delayAreaAssetId);
		} else {
			setError(resp.message);
		}
		setIsDeleting(false);
		setDeleteModalOpen(false);
		setNodeToDelete(null);
	}, [nodeToDelete, selectedNode, delayAreaAssetId, searchDelays, showNode]);

	const handleCancelDelete = useCallback(() => {
		setDeleteModalOpen(false);
		setNodeToDelete(null);
	}, []);

	const handleSave = useCallback(
		async (delayCode: CatalogDelayCode) => {
			setIsSaving(true);
			if (isCreating) {
				const resp = await upsertDelay(delayCode);
				if (resp.ok) {
					setNotification("Delay code created successfully");
					setIsCreating(false);
					showNode(null);
					await searchDelays(delayAreaAssetId);
				} else {
					setError(resp.message);
				}
			} else {
				// Autosave already handled in DetailViewer — just refresh the tree
				await searchDelays(delayAreaAssetId);
			}
			setIsSaving(false);
		},
		[isCreating, delayAreaAssetId, searchDelays, showNode],
	);

	const handleClose = useCallback(() => {
		showNode(null);
		setIsCreating(false);
	}, [showNode]);

	const handleAddClick = useCallback(() => {
		setDelayNodeSelectedInTree(null);
		showNode(null);
		setIsCreating(true);
	}, [setDelayNodeSelectedInTree, showNode]);

	const handleToggleQuickButton = useCallback(
		async (node: CatalogDelayCode) => {
			const updated = {
				...node,
				QuickButtonEnabled: !node.QuickButtonEnabled,
			};
			const resp = await upsertDelay(updated);
			if (resp.ok) {
				setNotification(
					updated.QuickButtonEnabled
						? "Quick button enabled"
						: "Quick button removed",
				);
				await searchDelays(delayAreaAssetId);
			} else {
				setError(resp.message);
			}
		},
		[delayAreaAssetId, searchDelays],
	);

	const executeToggleActive = useCallback(
		async (node: CatalogDelayCode, cascade: boolean) => {
			const updated = { ...node, IsActive: !node.IsActive };
			const resp = await upsertDelay(updated, cascade);
			if (resp.ok) {
				const action = updated.IsActive ? "activated" : "deactivated";
				const scope = cascade ? " (including children)" : "";
				setNotification(`Delay code ${action}${scope}`);
				// Refresh the detail panel if this node is selected
				if (selectedNode?.DelayCodeId === node.DelayCodeId) {
					setSelectedNode(updated);
				}
				await searchDelays(delayAreaAssetId);
			} else {
				setError(resp.message);
			}
		},
		[delayAreaAssetId, searchDelays, selectedNode],
	);

	const handleToggleActive = useCallback(
		async (node: CatalogDelayCode) => {
			// Activating: check parent is active first
			if (!node.IsActive && node.ParentDelayCodeId) {
				const parent = allDelayCodes.find(
					(n: CatalogDelayCode) => n.DelayCodeId === node.ParentDelayCodeId,
				);
				if (parent && !parent.IsActive) {
					setError(
						"Cannot activate a delay code whose parent is inactive. Activate the parent first.",
					);
					return;
				}
			}
			// Branch → show modal with "only this" vs "all children" options
			if (node.IsBranch) {
				setNodeToDeactivate(node);
				setDeactivateModalOpen(true);
				return;
			}
			await executeToggleActive(node, false);
		},
		[executeToggleActive, allDelayCodes],
	);

	const handleToggleActiveOnlyThis = useCallback(async () => {
		if (!nodeToDeactivate) return;
		setIsDeactivating(true);
		await executeToggleActive(nodeToDeactivate, false);
		setIsDeactivating(false);
		setDeactivateModalOpen(false);
		setNodeToDeactivate(null);
	}, [nodeToDeactivate, executeToggleActive]);

	const handleToggleActiveWithChildren = useCallback(async () => {
		if (!nodeToDeactivate) return;
		setIsDeactivating(true);
		await executeToggleActive(nodeToDeactivate, true);
		setIsDeactivating(false);
		setDeactivateModalOpen(false);
		setNodeToDeactivate(null);
	}, [nodeToDeactivate, executeToggleActive]);

	const handleCancelDeactivate = useCallback(() => {
		setDeactivateModalOpen(false);
		setNodeToDeactivate(null);
	}, []);

	return (
		<Box
			sx={{
				width: "100%",
				height: "calc(100vh - 130px)",
				display: "flex",
				flexDirection: "column",
				p: 1,
			}}
		>
			<HelmetDexteel title="Delay Codes Tree" />

			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 1,
				}}
			>
				<Typography variant="h5" sx={{ fontWeight: 600 }}>
					Stops Codes
					{delayAreaAssetName ? ` - ${delayAreaAssetName}` : ""}
				</Typography>
				<Button
					color="primary"
					variant="contained"
					size="small"
					startIcon={<AddIcon />}
					onClick={handleAddClick}
				>
					ADD DELAY CODE
				</Button>
			</Box>

			<Group
				id="delay-codes-manager-group"
				orientation="horizontal"
				defaultLayout={defaultLayout ?? DEFAULT_LAYOUT}
				onLayoutChange={onLayoutChange}
				style={{ flex: 1, width: "100%" }}
			>
				<Panel id="left" minSize={25} style={{ paddingBottom: 5 }}>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							height: "100%",
							pr: 1,
						}}
					>
						<Box
							sx={{
								mb: 1,
								display: "flex",
								alignItems: "center",
								gap: 2,
							}}
						>
							<DelayCodesSearchInput
								allDelayCodes={searchableDelayCodes}
								onSearchResultClick={(result) => {
									handleNodeSelect(result.node);
									setScrollToNodeId(result.id);
								}}
							/>

							<ShowOnlyActivesCheckbox
								checked={onlyActives}
								onChange={(value) => setFilters({ onlyActives: value })}
							/>
						</Box>
						<Paper
							elevation={1}
							sx={{
								flex: 1,
								width: "100%",
								overflow: "hidden",
							}}
						>
							<TreeViewDelay
								delayAreaAssetId={delayAreaAssetId}
								delayAreaAssetName={delayAreaAssetName}
								onNodeSelect={handleNodeSelect}
								onRequestAdd={handleRequestAdd}
								onRequestDelete={handleRequestDelete}
								onToggleQuickButton={handleToggleQuickButton}
								onToggleActive={handleToggleActive}
								onError={setError}
								onNotification={setNotification}
								scrollToNodeId={scrollToNodeId}
								onlyActives={onlyActives}
							/>
						</Paper>
					</Box>
				</Panel>

				<Separator
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "col-resize",
					}}
				>
					<Box
						sx={{
							width: 6,
							height: 40,
							borderRadius: 3,
							backgroundColor: "divider",
							"&:hover": { backgroundColor: "primary.main" },
						}}
					/>
				</Separator>

				<Panel id="right" minSize={30} style={{ paddingBottom: 5 }}>
					<Box sx={{ pl: 1, height: "100%" }}>
						<Paper
							elevation={1}
							sx={{
								height: "100%",
								overflowY: "scroll",
								overflowX: "hidden",
							}}
						>
							<DelayCodeDetailViewer
								delayCode={selectedNode}
								isCreating={isCreating}
								isSaving={isSaving}
								onSave={handleSave}
								onClose={handleClose}
								onError={setError}
								onNotification={setNotification}
								onToggleActive={handleToggleActive}
							/>
						</Paper>
					</Box>
				</Panel>
			</Group>

			<DeleteConfirmModal
				open={deleteModalOpen}
				title="DELETE DELAY CODE"
				itemName={nodeToDelete?.DelayCodeName ?? ""}
				isDeleting={isDeleting}
				onConfirm={handleConfirmDelete}
				onCancel={handleCancelDelete}
			/>

			<MesfModal
				title={
					nodeToDeactivate?.IsActive
						? "DEACTIVATE DELAY CODE"
						: "ACTIVATE DELAY CODE"
				}
				open={deactivateModalOpen}
				handleClose={handleCancelDeactivate}
				id="toggle-active-confirm-modal"
				maxWidth="sm"
			>
				<MesfModal.Content style={{ padding: "20px 30px" }}>
					<Typography>
						{nodeToDeactivate?.IsActive
							? "Do you want to deactivate only "
							: "Do you want to activate only "}
						<strong>{nodeToDeactivate?.DelayCodeName}</strong>, or include all
						its children?
					</Typography>
				</MesfModal.Content>
				<MesfModal.Actions style={{ padding: "15px 30px 20px" }}>
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							width: "100%",
						}}
					>
						<Button
							variant="contained"
							onClick={handleCancelDeactivate}
							disabled={isDeactivating}
							sx={{
								minWidth: 130,
								backgroundColor: "#e0e0e0",
								color: "rgba(0, 0, 0, 0.87)",
								"&:hover": {
									backgroundColor: "#d5d5d5",
								},
							}}
						>
							CANCEL
						</Button>
						<Box sx={{ display: "flex", gap: 1 }}>
							<Button
								variant="contained"
								color="primary"
								onClick={handleToggleActiveOnlyThis}
								disabled={isDeactivating}
								sx={{ minWidth: 130 }}
							>
								ONLY THIS
							</Button>
							<Button
								variant="contained"
								color="warning"
								onClick={handleToggleActiveWithChildren}
								disabled={isDeactivating}
								sx={{ minWidth: 130 }}
								startIcon={
									isDeactivating && (
										<CircularProgress size={16} color="inherit" />
									)
								}
							>
								ALL CHILDREN
							</Button>
						</Box>
					</Box>
				</MesfModal.Actions>
			</MesfModal>

			<NotificationSnackBar
				message={notification}
				onHide={() => setNotification("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</Box>
	);
};
